/**
 * Structured Logger Utility - Enhanced Version
 *
 * A production-ready logging utility with:
 * - Configurable log levels (runtime adjustment)
 * - Correlation ID support for request tracing
 * - Performance measurement utilities
 * - Log buffering and batching
 * - Structured JSON output option
 * - Log sampling for high-volume scenarios
 * - Memory-safe context handling
 * - Sentry integration for error tracking
 *
 * @example
 * ```ts
 * // Basic logging
 * logger.info('User logged in', { userId: '123' });
 *
 * // With correlation ID
 * logger.setCorrelationId('abc-123');
 * logger.info('Processing request'); // Includes correlationId automatically
 *
 * // Performance measurement
 * const timer = logger.startTimer('database-query');
 * await db.query(...);
 * timer.end({ rowCount: 100 }); // Logs duration automatically
 *
 * // Scoped logging
 * const scopedLog = logger.child('AuthService');
 * scopedLog.info('Authenticating user'); // Prefixed with [AuthService]
 * ```
 *
 * Optimized: January 20, 2026
 */

// ============================================
// TYPES
// ============================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

/** Numeric priority for log levels (higher = more important) */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    silent: 4,
};

export interface LogContext {
    [key: string]: unknown;
}

/** Configuration options for the logger */
export interface LoggerConfig {
    /** Minimum log level to output (default: 'debug' in dev, 'info' in prod) */
    minLevel: LogLevel;
    /** Enable JSON output format (default: false in dev, true in prod) */
    jsonOutput: boolean;
    /** Enable log buffering (default: false) */
    enableBuffering: boolean;
    /** Buffer flush interval in ms (default: 5000) */
    bufferFlushInterval: number;
    /** Maximum buffer size before forced flush (default: 100) */
    maxBufferSize: number;
    /** Sampling rate for debug logs in production (0-1, default: 0.1) */
    debugSampleRate: number;
    /** Maximum context object depth for serialization (default: 3) */
    maxContextDepth: number;
    /** Maximum context string length (default: 10000) */
    maxContextLength: number;
    /** Include stack traces in debug logs (default: false in prod) */
    includeStackTraces: boolean;
}

/** A single log entry */
interface LogEntry {
    timestamp: string;
    level: LogLevel;
    message: string;
    context?: LogContext;
    correlationId?: string;
    source?: string;
    error?: {
        name: string;
        message: string;
        stack?: string;
    };
}

/** Timer interface for performance measurement */
export interface PerformanceTimer {
    /** End the timer and log the duration */
    end: (additionalContext?: LogContext) => number;
    /** Get elapsed time without ending the timer */
    elapsed: () => number;
    /** Cancel the timer without logging */
    cancel: () => void;
}

/**
 * Type-safe interface for Sentry integration.
 * Matches the Sentry SDK's captureException signature.
 */
interface SentryLike {
    captureException: (
        error: Error | unknown,
        context?: { extra?: Record<string, unknown> }
    ) => void;
}

/**
 * Extended window interface with optional Sentry property.
 * Used for type-safe access to globally loaded Sentry SDK.
 */
interface WindowWithSentry extends Window {
    Sentry?: SentryLike;
}

// ============================================
// DEFAULT CONFIGURATION
// ============================================

const getDefaultConfig = (isProduction: boolean): LoggerConfig => ({
    minLevel: isProduction ? 'info' : 'debug',
    jsonOutput: isProduction,
    enableBuffering: false,
    bufferFlushInterval: 5000,
    maxBufferSize: 100,
    debugSampleRate: isProduction ? 0.1 : 1.0,
    maxContextDepth: 3,
    maxContextLength: 10000,
    includeStackTraces: !isProduction,
});

// ============================================
// LOGGER CLASS
// ============================================

class Logger {
    private static instance: Logger;
    private isProduction: boolean;
    private config: LoggerConfig;
    private buffer: LogEntry[] = [];
    private flushInterval: ReturnType<typeof setInterval> | null = null;
    private correlationId: string | null = null;
    private source: string | null = null;
    private activeTimers: Map<string, number> = new Map();

    private constructor(source?: string) {
        this.isProduction = import.meta.env.PROD;
        this.config = getDefaultConfig(this.isProduction);
        this.source = source ?? null;

        // Start buffer flush interval if buffering is enabled
        if (this.config.enableBuffering) {
            this.startBufferFlush();
        }
    }

    /**
     * Get the singleton logger instance
     */
    public static getInstance(): Logger {
        if (!Logger.instance) {
            Logger.instance = new Logger();
        }
        return Logger.instance;
    }

    // ============================================
    // CONFIGURATION
    // ============================================

    /**
     * Update logger configuration at runtime
     */
    public configure(partialConfig: Partial<LoggerConfig>): void {
        const oldConfig = { ...this.config };
        this.config = { ...this.config, ...partialConfig };

        // Handle buffering state changes
        if (this.config.enableBuffering && !oldConfig.enableBuffering) {
            this.startBufferFlush();
        } else if (!this.config.enableBuffering && oldConfig.enableBuffering) {
            this.stopBufferFlush();
            this.flush(); // Flush any remaining logs
        }
    }

    /**
     * Get current configuration (readonly)
     */
    public getConfig(): Readonly<LoggerConfig> {
        return { ...this.config };
    }

    /**
     * Set the minimum log level
     */
    public setLevel(level: LogLevel): void {
        this.config.minLevel = level;
    }

    /**
     * Get the current log level
     */
    public getLevel(): LogLevel {
        return this.config.minLevel;
    }

    // ============================================
    // CORRELATION ID
    // ============================================

    /**
     * Set a correlation ID for request tracing.
     * All subsequent logs will include this ID until cleared.
     */
    public setCorrelationId(id: string): void {
        this.correlationId = id;
    }

    /**
     * Get the current correlation ID
     */
    public getCorrelationId(): string | null {
        return this.correlationId;
    }

    /**
     * Clear the correlation ID
     */
    public clearCorrelationId(): void {
        this.correlationId = null;
    }

    /**
     * Generate a new correlation ID and set it
     */
    public generateCorrelationId(): string {
        const id = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        this.setCorrelationId(id);
        return id;
    }

    // ============================================
    // CHILD LOGGERS
    // ============================================

    /**
     * Create a child logger with a source prefix.
     * Useful for per-service or per-module logging.
     */
    public child(source: string): Logger {
        const childLogger = new Logger(source);
        childLogger.config = { ...this.config };
        childLogger.correlationId = this.correlationId;
        return childLogger;
    }

    // ============================================
    // PERFORMANCE MEASUREMENT
    // ============================================

    /**
     * Start a performance timer.
     * Returns an object with end(), elapsed(), and cancel() methods.
     *
     * @example
     * const timer = logger.startTimer('api-call');
     * await fetchData();
     * timer.end({ statusCode: 200 }); // Logs: "api-call completed in 123ms"
     */
    public startTimer(operation: string, context?: LogContext): PerformanceTimer {
        const startTime = performance.now();
        const timerId = `${operation}-${Date.now()}`;
        this.activeTimers.set(timerId, startTime);

        if (context) {
            this.debug(`${operation} started`, context);
        }

        return {
            end: (additionalContext?: LogContext): number => {
                const duration = this.endTimer(timerId, operation, additionalContext);
                return duration;
            },
            elapsed: (): number => {
                return performance.now() - startTime;
            },
            cancel: (): void => {
                this.activeTimers.delete(timerId);
            },
        };
    }

    private endTimer(timerId: string, operation: string, additionalContext?: LogContext): number {
        const startTime = this.activeTimers.get(timerId);
        if (startTime === undefined) {
            this.warn(`Timer "${operation}" was not found or already ended`);
            return 0;
        }

        const duration = performance.now() - startTime;
        this.activeTimers.delete(timerId);

        this.info(`${operation} completed`, {
            ...additionalContext,
            durationMs: Math.round(duration * 100) / 100, // Round to 2 decimal places
        });

        return duration;
    }

    /**
     * Get count of active timers (for debugging)
     */
    public getActiveTimerCount(): number {
        return this.activeTimers.size;
    }

    // ============================================
    // LOGGING METHODS
    // ============================================

    /**
     * Log a debug message (only in development or if sampled in production)
     */
    public debug(message: string, context?: LogContext): void {
        if (!this.shouldLog('debug')) return;

        // Apply sampling in production
        if (this.isProduction && Math.random() > this.config.debugSampleRate) {
            return;
        }

        this.log('debug', message, context);
    }

    /**
     * Log an info message
     */
    public info(message: string, context?: LogContext): void {
        if (!this.shouldLog('info')) return;
        this.log('info', message, context);
    }

    /**
     * Log a warning message
     */
    public warn(message: string, context?: LogContext): void {
        if (!this.shouldLog('warn')) return;
        this.log('warn', message, context);
    }

    /**
     * Log an error message with optional error object
     */
    public error(message: string, error?: Error | unknown, context?: LogContext): void {
        if (!this.shouldLog('error')) return;

        const errorInfo = this.extractErrorInfo(error);
        const errorContext = errorInfo
            ? { ...context, ...errorInfo }
            : context;

        this.log('error', message, errorContext, errorInfo ?? undefined);

        // Sentry integration for production error tracking
        if (this.isProduction && typeof window !== 'undefined' && error) {
            const sentryWindow = window as WindowWithSentry;
            sentryWindow.Sentry?.captureException(error, { extra: context });
        }
    }

    // ============================================
    // BUFFERING
    // ============================================

    /**
     * Manually flush the log buffer
     */
    public flush(): void {
        if (this.buffer.length === 0) return;

        const entries = [...this.buffer];
        this.buffer = [];

        entries.forEach(entry => this.outputEntry(entry));
    }

    /**
     * Get buffer size (for monitoring)
     */
    public getBufferSize(): number {
        return this.buffer.length;
    }

    private startBufferFlush(): void {
        if (this.flushInterval) return;
        this.flushInterval = setInterval(() => this.flush(), this.config.bufferFlushInterval);
    }

    private stopBufferFlush(): void {
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }
    }

    // ============================================
    // INTERNAL HELPERS
    // ============================================

    private shouldLog(level: LogLevel): boolean {
        return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.minLevel];
    }

    private log(
        level: LogLevel,
        message: string,
        context?: LogContext,
        errorInfo?: { name: string; message: string; stack?: string }
    ): void {
        const entry: LogEntry = {
            timestamp: new Date().toISOString(),
            level,
            message: this.source ? `[${this.source}] ${message}` : message,
            context: context ? this.sanitizeContext(context) : undefined,
            correlationId: this.correlationId ?? undefined,
            source: this.source ?? undefined,
            error: errorInfo,
        };

        if (this.config.enableBuffering && level !== 'error') {
            this.buffer.push(entry);
            if (this.buffer.length >= this.config.maxBufferSize) {
                this.flush();
            }
        } else {
            this.outputEntry(entry);
        }
    }

    private outputEntry(entry: LogEntry): void {
        const output = this.config.jsonOutput
            ? this.formatJson(entry)
            : this.formatHuman(entry);

        switch (entry.level) {
            case 'debug':
                console.debug(output);
                break;
            case 'info':
                console.log(output);
                break;
            case 'warn':
                console.warn(output);
                break;
            case 'error':
                console.error(output);
                break;
        }
    }

    private formatJson(entry: LogEntry): string {
        return JSON.stringify({
            ...entry,
            // Flatten error into the main object for easier querying
            ...(entry.error && {
                errorName: entry.error.name,
                errorMessage: entry.error.message,
                errorStack: entry.error.stack,
            }),
        });
    }

    private formatHuman(entry: LogEntry): string {
        const parts = [
            `[${entry.timestamp}]`,
            `[${entry.level.toUpperCase()}]`,
        ];

        if (entry.correlationId) {
            parts.push(`[${entry.correlationId.slice(0, 8)}]`);
        }

        parts.push(entry.message);

        if (entry.context && Object.keys(entry.context).length > 0) {
            parts.push(JSON.stringify(entry.context));
        }

        if (entry.error?.stack && this.config.includeStackTraces) {
            parts.push(`\n${entry.error.stack}`);
        }

        return parts.join(' ');
    }

    private extractErrorInfo(error: unknown): { name: string; message: string; stack?: string } | null {
        if (!error) return null;

        if (error instanceof Error) {
            return {
                name: error.name,
                message: error.message,
                stack: this.config.includeStackTraces ? error.stack : undefined,
            };
        }

        if (typeof error === 'object' && error !== null) {
            const errorObj = error as Record<string, unknown>;
            return {
                name: String(errorObj.name ?? 'UnknownError'),
                message: String(errorObj.message ?? JSON.stringify(error)),
            };
        }

        return {
            name: 'UnknownError',
            message: String(error),
        };
    }

    /**
     * Sanitize context to prevent memory issues with large objects
     */
    private sanitizeContext(context: LogContext): LogContext {
        try {
            const serialized = JSON.stringify(context, this.createReplacer(this.config.maxContextDepth));
            if (serialized.length > this.config.maxContextLength) {
                return {
                    _truncated: true,
                    _originalLength: serialized.length,
                    data: JSON.parse(serialized.slice(0, this.config.maxContextLength) + '"}'),
                };
            }
            return JSON.parse(serialized);
        } catch {
            return { _serializationError: true, type: typeof context };
        }
    }

    private createReplacer(maxDepth: number): (key: string, value: unknown) => unknown {
        const seen = new WeakSet();
        let depth = 0;

        return function (this: unknown, key: string, value: unknown): unknown {
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[Circular]';
                }
                seen.add(value);

                // Track depth (approximate - not perfect but good enough)
                if (key !== '') {
                    depth++;
                    if (depth > maxDepth) {
                        depth--;
                        return '[Max Depth]';
                    }
                }
            }

            // Handle special values
            if (typeof value === 'function') {
                return '[Function]';
            }
            if (value instanceof Error) {
                return { name: value.name, message: value.message };
            }
            if (typeof value === 'bigint') {
                return value.toString();
            }

            return value;
        };
    }

    // ============================================
    // CLEANUP
    // ============================================

    /**
     * Cleanup method - flush buffer and stop intervals.
     * Call this before app shutdown if using buffering.
     */
    public shutdown(): void {
        this.stopBufferFlush();
        this.flush();
        this.activeTimers.clear();
    }
}

// ============================================
// EXPORTS
// ============================================

export const logger = Logger.getInstance();

/**
 * Utility function to create a scoped logger for a service/module
 */
export const createScopedLogger = (source: string): Logger => {
    return logger.child(source);
};

/**
 * Utility function to wrap an async operation with performance logging
 */
export const withPerformanceLogging = async <T>(
    operation: string,
    fn: () => Promise<T>,
    context?: LogContext
): Promise<T> => {
    const timer = logger.startTimer(operation, context);
    try {
        const result = await fn();
        timer.end({ success: true });
        return result;
    } catch (error) {
        timer.end({ success: false, error: error instanceof Error ? error.message : 'Unknown error' });
        throw error;
    }
};

/**
 * Decorator-style function for logging function calls
 * (useful for debugging during development)
 */
export const logFunctionCall = <TArgs extends unknown[], TReturn>(
    fnName: string,
    fn: (...args: TArgs) => TReturn
): ((...args: TArgs) => TReturn) => {
    return (...args: TArgs): TReturn => {
        logger.debug(`${fnName} called`, { argCount: args.length });
        try {
            const result = fn(...args);
            if (result instanceof Promise) {
                return result.then(r => {
                    logger.debug(`${fnName} resolved`);
                    return r;
                }).catch(e => {
                    logger.debug(`${fnName} rejected`, { error: e instanceof Error ? e.message : String(e) });
                    throw e;
                }) as TReturn;
            }
            logger.debug(`${fnName} returned`);
            return result;
        } catch (error) {
            logger.debug(`${fnName} threw`, { error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    };
};
